// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetRecentColleagues } from "@src/appV2/Agents/api/useGetRecentColleagues";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useGetPaginatedBookabilityStatusForShifts } from "@src/appV2/OpenShifts/ShiftAction/api/useGetBookabilityStatusForShifts";
import {
  SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useFavoriteWorkplaces } from "@src/appV2/Workplaces/api/useFavoriteWorkplaces";
import { useQueryClient } from "@tanstack/react-query";
import constate from "constate";
import { useRef } from "react";
import { type ListRange } from "react-virtuoso";

import { getCachedShiftRouteEstimate } from "../../ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { useGetFriendsList } from "../../WorkWithFriends/hooks/useGetFriendsList";
import { type BundleShiftPay, type ShiftBundle } from "../Bundle/types";
import { MANDATORY_BREAK_DURATION_IN_MINUTES } from "../constants";
import { convertOpenShiftToModalShiftData } from "../convertOpenShiftToModalShiftData";
import { computeDoubleShiftPays, computeShiftPay } from "../Double/computeDoubleShiftPays";
import { type ShiftDouble } from "../Double/types";
import {
  type Offer,
  type OpenShift,
  type OpenShiftListItem,
  type OpenShiftWorkplace,
  type RateNegotiation,
} from "./types";
import { type ShiftSlotCoalesced, useGetPaginatedShiftSlotsV2 } from "./useGetShiftsSlotV2";
import { useOpenShiftsBlockingRequirementsContext } from "./useOpenShiftsBlockingRequirementsContext";
import { useShiftSlotsV2PageSize } from "./useShiftSlotsV2PageSize";

interface OnClickOpenShiftParams {
  shift: OpenShift;
  workplace: OpenShiftWorkplace;
  offer?: Offer;
  shiftAuction?: ShiftAuction;
}

interface OpenShiftListData {
  openShifts: OpenShiftListItem[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
  rateNegotiationsMap: Map<string, RateNegotiation>;
  shiftAuctionsMap?: Map<string, ShiftAuction>;
  bundlesByShiftId?: Map<string, ShiftBundle>;
  doublesByShiftId?: Map<string, ShiftDouble>;
  offersMap?: Map<string, Offer>;
  canLoadMoreShifts?: boolean;
  isLoadingMoreShifts?: boolean;
  loadMoreShifts?: () => void;
  isOpenShiftsLoading?: boolean;
  isOpenShiftsError?: boolean;
  isOpenShiftsSuccess?: boolean;
}

/**
 * Encapsulates data for open shift lists, and logic for opening a shift modal.
 *
 * Accepts a list that may include both open shifts and in-app ads (OpenShiftListItem[]),
 * but filters out ads for business logic operations (bookability, shift slots, analytics)
 * since these only apply to actual shifts.
 *
 * Fetches bookability status and missing requirements for given open shifts,
 * and provides them in a map of shift id to data, which is used by the
 * shift card component.
 *
 * Also provides data for loading more shifts, used by the virtualized list.
 *
 * When opening a shift modal, the function pre-populates the modal
 * with data so that it is ready as soon as it's opened.
 */
function useOpenShiftListData(props: OpenShiftListData) {
  const {
    openShifts: allItems,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap = new Map<string, ShiftAuction>(),
    bundlesByShiftId = new Map<string, ShiftBundle>(),
    doublesByShiftId = new Map<string, ShiftDouble>(),
    offersMap = new Map<string, Offer>(),
    canLoadMoreShifts = false,
    isLoadingMoreShifts = false,
    loadMoreShifts,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
  } = props;

  // Filter out ads for business logic (bookability, shift slots, analytics)
  // These only apply to actual shifts, not ads
  const actualOpenShifts = allItems.filter(
    (item): item is OpenShift & { type: "open-shift" } => item.type === "open-shift"
  );

  const { userId: workerId, hideAsColleagueOnShifts } = useDefinedWorker();

  const { navigateToModal, setInitialShiftModalsData } = useShiftModalsDataContext();
  const { favoriteWorkplaces } = useFavoriteWorkplaces();
  const favoriteWorkplaceIds = new Set(
    favoriteWorkplaces.map((favoriteWorkplace) => favoriteWorkplace.workplaceId)
  );

  const lastThresholdRef = useRef<number>(0);
  const shiftsSlotsMapRef = useRef<Map<string, ShiftSlotCoalesced>>(new Map());
  const shiftsSlotsMap = shiftsSlotsMapRef.current;

  const shiftSlotsV2PageSize = useShiftSlotsV2PageSize();

  const {
    shiftBookabilityById,
    queryResult: {
      fetchNextPage: fetchNextBookabilityPage,
      hasNextPage: hasNextBookabilityPage,
      isFetchingNextPage: isFetchingBookabilityPage,
    },
  } = useGetPaginatedBookabilityStatusForShifts({
    shiftIds: actualOpenShifts.map((shift) => shift.id),
    agentId: workerId,
    returnFirstUnmetCriteria: false,
  });
  const shiftsBookabilityMap = new Map(Object.entries(shiftBookabilityById));

  const {
    shiftSlotsById,
    queryResult: { fetchNextPage: fetchNextShiftSlotsPage },
  } = useGetPaginatedShiftSlotsV2({
    shiftIds: actualOpenShifts.map((shift) => shift.id),
  });

  Object.entries(shiftSlotsById).forEach(([key, value]) => {
    shiftsSlotsMap.set(key, value);
  });

  const { data: friendsList } = useGetFriendsList();
  const friendsMap = new Map(friendsList?.data.map((friend) => [friend.id, friend]));

  const shiftsBlockingRequirementsContextMap = useOpenShiftsBlockingRequirementsContext({
    openShifts: actualOpenShifts,
    workplacesMap,
  });

  const { data: recentColleaguesData } = useGetRecentColleagues({
    enabled: !hideAsColleagueOnShifts,
  });

  const recentColleaguesMap = new Map(
    recentColleaguesData?.map((colleague) => [colleague.colleagueUserId, colleague])
  );

  const loadMore = () => {
    loadMoreShifts?.();
    void fetchNextBookabilityPage();
  };

  const handleRangeChanged = (range: ListRange) => {
    const currentThreshold =
      Math.ceil(range.startIndex / shiftSlotsV2PageSize) * shiftSlotsV2PageSize;
    if (currentThreshold > lastThresholdRef.current) {
      lastThresholdRef.current = currentThreshold;
      void fetchNextShiftSlotsPage();
    }
  };

  const canLoadMore = canLoadMoreShifts || hasNextBookabilityPage;
  const isLoadingMore = isLoadingMoreShifts || isFetchingBookabilityPage;

  const queryClient = useQueryClient();

  const onClickOpenShift = (params: OnClickOpenShiftParams) => {
    const { shift, workplace, offer, shiftAuction } = params;

    const bundle = bundlesByShiftId.get(shift.id);

    const rateNegotiation = rateNegotiationsMap.get(
      shift.relationships["rate-negotiation"]?.data.id ?? ""
    );

    const modalShiftData = convertOpenShiftToModalShiftData({
      shift,
      workplace,
      offer,
      rateNegotiation,
      // Use default break duration - modal will fetch actual value
      mandatoryBreakDurationInMinutes: MANDATORY_BREAK_DURATION_IN_MINUTES,
      shiftAuction,
    });

    const blockingRequirementsContext = shiftsBlockingRequirementsContextMap.get(shift.id);

    logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_OPENED, {
      shiftId: shift.id,
      shift: modalShiftData,
      bookabilityStatus: shiftsBookabilityMap.get(shift.id),
      blockingRequirements: blockingRequirementsContext?.blockingRequirements,
    });

    const computedArrivalData = getCachedShiftRouteEstimate({
      queryClient,
      shiftOrShiftInviteId: shift.id,
    });

    if (isDefined(bundle)) {
      const bundleShiftPays = new Map<string, BundleShiftPay>();
      const requiresLunchBreak = workplace.attributes.requiresLunchBreak ?? false;

      for (const bundleShift of bundle.shifts) {
        const offerId = bundleShift.relationships.offer?.data.id;
        if (!offerId) {
          continue;
        }

        const shiftOffer = offersMap.get(offerId);
        if (!shiftOffer) {
          continue;
        }

        bundleShiftPays.set(
          bundleShift.id,
          computeShiftPay(
            { start: bundleShift.attributes.start, end: bundleShift.attributes.end },
            shiftOffer.attributes.pay.amountInMinorUnits,
            requiresLunchBreak
          )
        );
      }

      setInitialShiftModalsData({
        shift: modalShiftData,
        bookabilityStatus: shiftsBookabilityMap.get(shift.id),
        shiftSlots: shiftsSlotsMap.get(shift.id),
        friendsMap,
        blockingRequirementsContext: blockingRequirementsContext ?? undefined,
        computedArrivalData,
        bundle,
        bundleShiftPays,
      });

      navigateToModal(SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });

      return;
    }

    const double = doublesByShiftId.get(shift.id);
    const requiresLunchBreak = workplace.attributes.requiresLunchBreak ?? false;

    // Calculate pay data for double if it exists
    const doubleWithPay = isDefined(double)
      ? {
          ...double,
          ...computeDoubleShiftPays({
            double,
            anchorShift: { start: shift.attributes.start, end: shift.attributes.end },
            anchorOffer: offer,
            offersMap,
            requiresLunchBreak,
          }),
        }
      : undefined;

    setInitialShiftModalsData({
      shift: modalShiftData,
      bookabilityStatus: shiftsBookabilityMap.get(shift.id),
      shiftSlots: shiftsSlotsMap.get(shift.id),
      friendsMap,
      blockingRequirementsContext: blockingRequirementsContext ?? undefined,
      computedArrivalData,
      double: doubleWithPay,
    });

    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, { pathParams: { shiftId: shift.id } });
  };

  const onClickDouble = (params: OnClickOpenShiftParams) => {
    const { shift, workplace, offer } = params;
    const double = doublesByShiftId.get(shift.id);

    if (!isDefined(double)) {
      return;
    }

    const rateNegotiation = rateNegotiationsMap.get(
      shift.relationships["rate-negotiation"]?.data.id ?? ""
    );
    const modalShiftData = convertOpenShiftToModalShiftData({
      shift,
      workplace,
      offer,
      rateNegotiation,
      mandatoryBreakDurationInMinutes: MANDATORY_BREAK_DURATION_IN_MINUTES,
    });

    const requiresLunchBreak = workplace.attributes.requiresLunchBreak ?? false;

    const blockingRequirementsContext = shiftsBlockingRequirementsContextMap.get(shift.id);
    const computedArrivalData = getCachedShiftRouteEstimate({
      queryClient,
      shiftOrShiftInviteId: shift.id,
    });

    const companionBookabilityStatuses = new Map(
      double.companionShifts
        .map((companion) => {
          const status = shiftsBookabilityMap.get(companion.id);
          return status ? ([companion.id, status] as const) : undefined;
        })
        .filter(isDefined)
    );

    setInitialShiftModalsData({
      shift: modalShiftData,
      bookabilityStatus: shiftsBookabilityMap.get(shift.id),
      shiftSlots: shiftsSlotsMap.get(shift.id),
      friendsMap,
      blockingRequirementsContext: blockingRequirementsContext ?? undefined,
      computedArrivalData,
      companionBookabilityStatuses,
      double: {
        ...double,
        ...computeDoubleShiftPays({
          double,
          anchorShift: { start: shift.attributes.start, end: shift.attributes.end },
          anchorOffer: offer,
          offersMap,
          requiresLunchBreak,
        }),
      },
    });

    navigateToModal(SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH, {
      pathParams: { shiftId: shift.id },
    });
  };

  return {
    canLoadMore,
    isLoadingMore,
    loadMore,
    onClickOpenShift,
    onClickDouble,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    shiftsBookabilityMap,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    favoriteWorkplaceIds,
    shiftAuctionsMap,
    bundlesByShiftId,
    doublesByShiftId,
    handleRangeChanged,
  };
}

export const [OpenShiftListDataProvider, useOpenShiftListDataContext] =
  constate(useOpenShiftListData);
